/** Gamification + notifications hooks (trader side). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";

export interface LbRow { rank: number; userId: number; name: string; score: number; trades: number; wins: number; winRate: number; volume: number }
export interface Tournament { id: number; name: string; description: string | null; metric: string; accountType: string; prizePoolUsd: number; prizes: { rank: number; prize: string }[]; startAt: string; endAt: string; status: string }
export interface Achievement { key: string; icon: string; title: string; desc: string; got: boolean }
export interface Notif { id: number; kind: string; title: string; body: string | null; link: string | null; readAt: string | null; createdAt: string }

export function useLeaderboard(period: string, metric: string, account: string) {
  return useQuery({
    queryKey: ["game", "lb", period, metric, account],
    queryFn: async () => unwrap<{ board: LbRow[]; me: LbRow | null }>((await api.get(`/user/game/leaderboard?period=${period}&metric=${metric}&account=${account}`)).data),
    refetchInterval: 15_000,
  });
}
export function useTournaments() {
  return useQuery({ queryKey: ["game", "tournaments"], queryFn: async () => unwrap<Tournament[]>((await api.get("/user/game/tournaments")).data) });
}
export function useTournament(id: number | null) {
  return useQuery({
    queryKey: ["game", "tournament", id], enabled: id != null,
    queryFn: async () => unwrap<{ tournament: Tournament; board: LbRow[]; me: LbRow | null }>((await api.get(`/user/game/tournaments/${id}`)).data),
    refetchInterval: 15_000,
  });
}
export function useAchievements() {
  return useQuery({ queryKey: ["game", "achievements"], queryFn: async () => unwrap<{ achievements: Achievement[]; stats: any }>((await api.get("/user/game/achievements")).data) });
}

export function useNotifications() {
  return useQuery({ queryKey: ["notifs"], queryFn: async () => unwrap<Notif[]>((await api.get("/user/notifications")).data), refetchInterval: 20_000 });
}
export function useUnreadCount() {
  return useQuery({ queryKey: ["notifs", "unread"], queryFn: async () => unwrap<{ count: number }>((await api.get("/user/notifications/unread")).data), refetchInterval: 20_000 });
}
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids?: number[]) => (await api.post("/user/notifications/read", ids ? { ids } : {})).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["notifs"] }); },
  });
}
